import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { BookOpen, FileText, Activity, Link as LinkIcon, Video, Upload, Download, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import VictoryCelebration from '@/components/VictoryCelebration';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const VICTORY_TYPES = [
  { id: "first_doc_ai", label: "📄 Primeiro PDF Gerado com IA" },
  { id: "deep_research", label: "🔎 Primeira Pesquisa Profunda" },
  { id: "image_gen", label: "🎨 Criação de Imagens" },
  { id: "video_gen", label: "🎥 Criação de Vídeos" },
  { id: "presentation_gen", label: "📊 Apresentação Automática" },
  { id: "site_gen", label: "🌐 Site Criado com IA" },
  { id: "automation_gen", label: "⚡ Automação Ativa" },
  { id: "personal_agent", label: "🤖 Meu Agente Pessoal" },
  { id: "api_usage", label: "🔌 Mestre de Integrações" },
  { id: "full_app", label: "🚀 App Completo" },
  { id: "multi_agent", label: "🧠 Orquestrador de Agentes" },
  { id: "biz_ai_plan", label: "💼 Plano Estratégico AI" },
  { id: "ai_first_area", label: "🏢 Líder AI First na Área" },
  { id: "ai_first_biz", label: "🏆 Expert em Negócios AI" },
  // New Achievements
  { id: "prompt_architect", label: "⌨️ Arquiteto de Prompts" },
  { id: "data_weaver", label: "🕸️ Mestre dos Dados" },
  { id: "tool_master", label: "🛠️ Mestre das Ferramentas" },
  { id: "efficiency_expert", label: "⏱️ Expert em Eficiência" },
  { id: "ai_strategist", label: "🧭 Estrategista de IA" }
];

export default function ModuleDetail({ user }) {
  const { moduleId } = useParams();
  const [module, setModule] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [uploadingActivity, setUploadingActivity] = useState(null);
  const [evidenceCategories, setEvidenceCategories] = useState({});
  const [unlockedAchievement, setUnlockedAchievement] = useState(null);

  useEffect(() => {
    const fetchModule = async () => {
      try {
        const response = await axios.get(`${API}/modules/${moduleId}`, { withCredentials: true });
        setModule(response.data);
      } catch (error) {
        console.error('Error fetching module:', error);
      }
    };
    fetchModule();
  }, [moduleId]);

  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        const response = await axios.get(`${API}/submissions`, { withCredentials: true });
        setSubmissions(response.data.filter(s => s.module_id === moduleId));
      } catch (error) {
        console.error('Error fetching submissions:', error);
      }
    };
    if (moduleId) fetchSubmissions();
  }, [moduleId]);

  const handleFileUpload = async (activityId, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('module_id', moduleId);
    formData.append('activity_id', activityId);

    if (evidenceCategories[activityId]) {
      formData.append('evidence_category', evidenceCategories[activityId]);
    }

    try {
      setUploadingActivity(activityId);
      const res = await axios.post(`${API}/submissions/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data.unlocked_achievement) {
        setUnlockedAchievement(res.data.unlocked_achievement);
      }

      toast.success('Arquivo enviado com sucesso!');

      const response = await axios.get(`${API}/submissions`, { withCredentials: true });
      setSubmissions(response.data.filter(s => s.module_id === moduleId));
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Erro ao enviar arquivo');
    } finally {
      setUploadingActivity(null);
    }
  };

  const getActivitySubmission = (activityId) => {
    return submissions.find(s => s.activity_id === activityId);
  };

  const handleSuperNerdPromptClick = async (e, text) => {
    e.preventDefault();
    const superNerdUrl = "https://gemini.google.com/gem/1GhK3GI6cviUQEEL95K7DuyuSq-0bog9E?usp=sharing";
    try {
      await navigator.clipboard.writeText(`Quero saber mais sobre este tópico de estudo: ${text}`);
      toast.success('Assunto copiado! Cole (Ctrl+V) no chat do Super Nerd.', { duration: 4500 });
      setTimeout(() => {
        window.open(superNerdUrl, '_blank', 'noopener,noreferrer');
      }, 1000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
      // Fallback
      window.open(superNerdUrl, '_blank', 'noopener,noreferrer');
    }
  };

  if (!module) {
    return (
      <div className="min-h-screen">
        <Navigation user={user} />
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent"></div>
            <p className="mt-4 text-sm text-muted-foreground">Carregando módulo...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navigation user={user} />

      <main className="container mx-auto px-4 md:px-8 py-16">
        <div className="mb-8 border-b border-border pb-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                Módulo {module.order}
              </div>
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight leading-none mb-4" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="module-detail-title">
                {module.title}
              </h1>
              <p className="text-xl text-muted-foreground mb-2">{module.subtitle}</p>
              <p className="text-base leading-relaxed">{module.description}</p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="rounded-none font-medium tracking-wide uppercase text-xs">
                <Download className="h-4 w-4 mr-2" />
                Materiais
              </Button>
            </div>
          </div>
        </div>

        <Tabs defaultValue="essential" className="w-full">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 mb-8 rounded-none h-auto">
            <TabsTrigger value="essential" className="rounded-none py-3" data-testid="tab-essential">
              <Video className="h-4 w-4 mr-2" />
              Aula Master
            </TabsTrigger>
            <TabsTrigger value="complementary" className="rounded-none py-3" data-testid="tab-complementary">
              <FileText className="h-4 w-4 mr-2" />
              Materiais de Apoio
            </TabsTrigger>
            <TabsTrigger value="activities" className="rounded-none py-3" data-testid="tab-activities">
              <Activity className="h-4 w-4 mr-2" />
              Prática & Entregas
            </TabsTrigger>
            <TabsTrigger value="references" className="rounded-none py-3" data-testid="tab-references">
              <LinkIcon className="h-4 w-4 mr-2" />
              Biblioteca & Referências
            </TabsTrigger>
          </TabsList>

          <TabsContent value="essential" className="space-y-6">
            {/* Vídeo da Aula Master */}
            {module.master_video_url ? (
              <div className="space-y-4">
                {module.master_video_type === 'youtube' ? (
                  <div className="aspect-video bg-card rounded-none border border-border overflow-hidden">
                    <iframe
                      className="w-full h-full"
                      src={module.master_video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                      title={module.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                ) : module.master_video_type === 'vimeo' ? (
                  <div className="aspect-video bg-card rounded-none border border-border overflow-hidden">
                    <iframe
                      className="w-full h-full"
                      src={module.master_video_url.replace('vimeo.com/', 'player.vimeo.com/video/')}
                      title={module.title}
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                ) : module.master_video_type === 'upload' ? (
                  <div className="aspect-video bg-card rounded-none border border-border overflow-hidden">
                    <video
                      className="w-full h-full"
                      controls
                      src={module.master_video_url && module.master_video_url.startsWith('http') ? module.master_video_url : `${BACKEND_URL}${module.master_video_url}`}
                    >
                      Seu navegador não suporta o elemento de vídeo.
                    </video>
                  </div>
                ) : (
                  <Card className="p-6 rounded-none border border-border bg-muted/50">
                    <div className="flex items-start gap-4">
                      <Video className="h-6 w-6 mt-1" strokeWidth={1.5} />
                      <div className="flex-1">
                        <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Aula Master</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                          Acesse o vídeo da aula master
                        </p>
                        <a
                          href={module.master_video_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-sm font-medium uppercase tracking-wide hover:underline"
                        >
                          Abrir Vídeo <LinkIcon className="h-4 w-4" />
                        </a>
                      </div>
                    </div>
                  </Card>
                )}
                {module.master_video_filename && (
                  <p className="text-xs text-muted-foreground">
                    📹 Arquivo: {module.master_video_filename}
                  </p>
                )}
              </div>
            ) : module.live_session_url ? (
              <Card className="p-6 rounded-none border border-border bg-muted/50">
                <div className="flex items-start gap-4">
                  <Video className="h-6 w-6 mt-1" strokeWidth={1.5} />
                  <div className="flex-1">
                    <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Aula Master</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Participe da aula master deste módulo
                    </p>
                    <a
                      href={module.live_session_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-medium uppercase tracking-wide hover:underline"
                    >
                      Acessar Sessão <LinkIcon className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </Card>
            ) : (
              <div className="aspect-video bg-card rounded-none border border-border flex flex-col items-center justify-center relative overflow-hidden">
                <div className="relative z-10 text-center p-6">
                  <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                    <Video className="text-primary-foreground" size={32} />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Aula Magna: {module.title}</h3>
                  <p className="text-muted-foreground">Vídeo será disponibilizado em breve</p>
                </div>
              </div>
            )}

            {module.order === 1 && (
              <Card className="p-6 rounded-none border border-border mt-6">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}><Video className="h-5 w-5 text-aift-blue" /> Vídeos da Aula Master</h3>
                <div className="space-y-3">
                  <a href="https://youtu.be/RT5noVbRKq8?si=Hl9844JLMg-dKO7b" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 bg-muted/30 hover:bg-muted/50 border border-white/5 transition-colors group">
                    <Video className="h-4 w-4 text-muted-foreground group-hover:text-aift-blue transition-colors" />
                    <span className="text-sm font-medium">Top 5 Business Trends For 2026</span>
                  </a>
                  <a href="https://www.youtube.com/watch?v=bz4km63gK1Q&t=284s" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 bg-muted/30 hover:bg-muted/50 border border-white/5 transition-colors group">
                    <Video className="h-4 w-4 text-muted-foreground group-hover:text-aift-blue transition-colors" />
                    <span className="text-sm font-medium">Elon Musk's Tesla Bot Gen 3 Huge New Confirmations, Optimus Academy Amazing!</span>
                  </a>
                </div>
              </Card>
            )}

            {module.order === 2 && (
              <Card className="p-6 rounded-none border border-border mt-6">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}><Activity className="h-5 w-5 text-aift-pink" /> Cases de Referência (Benchmark)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="p-4 bg-muted/30 border border-white/5">
                    <h4 className="font-bold text-sm text-aift-pink mb-1">Invisalign (Personalização em massa)</h4>
                    <p className="text-xs text-muted-foreground">IA analisa o escaneamento, planeja a movimentação dente a dente e gera arquivos para impressão 3D em escala.</p>
                  </div>
                  <div className="p-4 bg-muted/30 border border-white/5">
                    <h4 className="font-bold text-sm text-aift-pink mb-1">DBS Bank</h4>
                    <p className="text-xs text-muted-foreground">IA integrada ao core para hiperpersonalizar ofertas e prever necessidades financeiras.</p>
                  </div>
                  <div className="p-4 bg-muted/30 border border-white/5">
                    <h4 className="font-bold text-sm text-aift-pink mb-1">Moderna</h4>
                    <p className="text-xs text-muted-foreground">IA acelera descoberta e desenvolvimento (mRNA), encurtando ciclos de P&D.</p>
                  </div>
                  <div className="p-4 bg-muted/30 border border-white/5">
                    <h4 className="font-bold text-sm text-aift-pink mb-1">Klarna</h4>
                    <p className="text-xs text-muted-foreground">Automação massiva de atendimento com aumento de satisfação.</p>
                  </div>
                </div>
                <a href="https://www.youtube.com/watch?v=k9aur17dCzE&t=50s" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 bg-muted/30 hover:bg-muted/50 border border-white/5 transition-colors group">
                  <Video className="h-4 w-4 text-muted-foreground group-hover:text-aift-pink transition-colors" />
                  <span className="text-sm font-medium">Vídeo (Agentic AI): The Best Examples Agentic</span>
                </a>
              </Card>
            )}

            <Card className="p-8 rounded-none border border-border mt-6">
              <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>Conteúdo Essencial</h3>
              {module.essential_content && module.essential_content.topics ? (
                <ul className="space-y-4">
                  {module.essential_content.topics.map((topic, index) => (
                    <li key={index} className="flex items-start gap-3 text-base leading-relaxed">
                      <div className="mt-1.5 h-2 w-2 rounded-full bg-primary flex-shrink-0" />
                      <span>{topic}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground">Conteúdo essencial será apresentado na aula master.</p>
              )}
            </Card>

            <Card className="p-6 bg-primary/10 border border-primary/30 rounded-none">
              <p className="text-sm">
                💡 <strong>Dica:</strong> Faça anotações durante a aula master e utilize a área de atividades para enviar suas dúvidas.
              </p>
            </Card>
          </TabsContent>

          <TabsContent value="complementary" className="space-y-6">
            <TooltipProvider>
            <Card className="p-8 rounded-none border border-border">
              <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>Materiais de Apoio</h3>

              {/* Super Nerd Direct Link */}
              <div className="mb-8 p-6 bg-primary/5 border border-primary/20 rounded-none group hover:border-primary/40 transition-all">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary/10 rounded-none text-primary">
                    <BookOpen size={24} />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-bold mb-1">Super Nerd</h4>
                    <p className="text-sm text-muted-foreground mb-4">Acesse o ambiente de IA com todo o contexto e documentos curados para tirar dúvidas e gerar insights.</p>
                    <a
                      href="https://gemini.google.com/gem/1GhK3GI6cviUQEEL95K7DuyuSq-0bog9E?usp=sharing"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-primary hover:underline"
                    >
                      Abrir Super Nerd <LinkIcon className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Activity Notebook Link */}
              <div className="mb-8 p-6 bg-aift-orange/10 border border-aift-orange/30 rounded-none group hover:border-aift-orange/50 transition-all flex flex-col md:flex-row items-start justify-between gap-6">
                <div className="flex items-start gap-4 flex-1">
                  <div className="p-3 bg-aift-orange/20 rounded-none text-aift-orange">
                    <FileText size={24} />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-bold mb-1">Caderno de Atividades - {module?.order <= 3 ? "Parte 1" : "Parte 2"}</h4>
                    <p className="text-sm text-muted-foreground mb-4">Acesse o caderno de atividades com exercícios práticos correspondentes a este ciclo do workshop.</p>
                    <a
                      href={`/documents/Caderno_de_Atividades_${module?.order <= 3 ? "1" : "2"}.pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-aift-orange hover:underline"
                    >
                      Baixar PDF <Download className="h-4 w-4" />
                    </a>
                  </div>
                </div>

                <div className="flex flex-col items-center bg-black/20 p-4 rounded-none border border-white/5 md:min-w-[140px]">
                  <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest mb-3">Versão Digital (Notion)</p>
                  {module?.order <= 3 ? (
                    <img src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=https://assorted-poppyseed-f48.notion.site/4d5abdc729bc44f4a8dd02d80a182df4&margin=0`} alt="QR Code Dia 1" className="w-24 h-24 mb-3 rounded-sm opacity-90 group-hover:opacity-100 transition-opacity" />
                  ) : (
                    <img src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=https://assorted-poppyseed-f48.notion.site/de1f872c13774c83bbf89acf1ac39866&margin=0`} alt="QR Code Dia 2" className="w-24 h-24 mb-3 rounded-sm opacity-90 group-hover:opacity-100 transition-opacity" />
                  )}
                  <a
                    href={module?.order <= 3 ? "https://assorted-poppyseed-f48.notion.site/4d5abdc729bc44f4a8dd02d80a182df4" : "https://assorted-poppyseed-f48.notion.site/de1f872c13774c83bbf89acf1ac39866"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] text-aift-orange hover:underline uppercase tracking-wider flex items-center gap-1 font-bold"
                  >
                    Abrir Link <LinkIcon size={10} />
                  </a>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Traditional Topics */}
                {module.complementary_content?.topics?.map((topic, index) => {
                  return (
                    <Tooltip key={`topic-${index}`} delayDuration={300}>
                      <TooltipTrigger asChild>
                        <div
                          onClick={(e) => handleSuperNerdPromptClick(e, topic)}
                          className="flex items-start gap-4 p-4 border border-border rounded-none hover:border-primary/50 transition-colors cursor-pointer group block"
                        >
                          <div className="p-3 bg-muted rounded-none group-hover:bg-primary/20 transition-colors">
                            <FileText size={20} strokeWidth={1.5} className="group-hover:text-primary transition-colors" />
                          </div>
                          <div className="flex-1 h-full flex flex-col justify-between">
                            <h4 className="font-medium mb-1 group-hover:text-primary transition-colors">{topic}</h4>
                            <span className="text-[10px] text-primary uppercase tracking-wider font-medium flex items-center gap-1 mt-2">
                              Explorar com o Super Nerd <LinkIcon size={10} />
                            </span>
                          </div>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <p className="text-xs">Copiar tópico e abrir chat <strong>(Ctrl+V)</strong></p>
                      </TooltipContent>
                    </Tooltip>
                  );
                })}

                {/* Notebook Sources (Extracted) */}
                {module.complementary_content?.notebook_sources?.map((source, index) => {
                  const targetUrl = source.url; // external url if available
                  const isInternal = !source.url;
                  
                  if (isInternal) {
                    return (
                      <Tooltip key={`source-${index}`} delayDuration={300}>
                        <TooltipTrigger asChild>
                          <div
                            onClick={(e) => handleSuperNerdPromptClick(e, source.title)}
                            className="flex items-start gap-4 p-4 border border-border rounded-none hover:border-primary/50 transition-colors bg-muted/10 cursor-pointer group block"
                          >
                            <div className="p-3 bg-muted rounded-none group-hover:bg-primary/20 transition-colors">
                              <LinkIcon size={20} strokeWidth={1.5} className="group-hover:text-primary transition-colors" />
                            </div>
                            <div className="flex-1 flex flex-col justify-between h-full">
                              <h4 className="font-medium mb-2 group-hover:text-primary transition-colors">{source.title}</h4>
                              <div className="flex items-center justify-between mt-auto pt-2 border-t border-white/5">
                                <span className="text-[10px] text-muted-foreground uppercase tracking-widest opacity-70">Documento Interno</span>
                                <span className="text-[10px] text-primary uppercase tracking-wider flex items-center gap-1">Super Nerd <LinkIcon size={10} /></span>
                              </div>
                            </div>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          <p className="text-xs">Copiar fonte e abrir chat <strong>(Ctrl+V)</strong></p>
                        </TooltipContent>
                      </Tooltip>
                    );
                  } else {
                    return (
                      <a
                        key={`source-${index}`}
                        href={targetUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-start gap-4 p-4 border border-border rounded-none hover:border-primary/50 transition-colors bg-muted/10 cursor-pointer group block"
                      >
                        <div className="p-3 bg-muted rounded-none group-hover:bg-primary/20 transition-colors">
                          <LinkIcon size={20} strokeWidth={1.5} className="group-hover:text-primary transition-colors" />
                        </div>
                        <div className="flex-1 flex flex-col justify-between h-full">
                          <h4 className="font-medium mb-2 group-hover:text-primary transition-colors">{source.title}</h4>
                          <div className="flex items-center gap-1 mt-auto pt-2 border-t border-white/5">
                            <span className="text-[10px] text-primary font-medium uppercase tracking-wider flex items-center gap-1">
                              Ver Fonte Original <LinkIcon size={10} />
                            </span>
                          </div>
                        </div>
                      </a>
                    );
                  }
                })}
              </div>

              {(!module.complementary_content?.topics && !module.complementary_content?.notebook_sources) && (
                <p className="text-muted-foreground">Materiais complementares disponíveis em breve.</p>
              )}
            </Card>
            </TooltipProvider>
          </TabsContent>

          <TabsContent value="activities" className="space-y-6">
            {module.activities && module.activities.length > 0 ? (
              module.activities.map((activity, index) => {
                const submission = getActivitySubmission(activity.id || `activity_${index}`);

                return (
                  <Card key={index} className="p-8 rounded-none border border-border">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <span className="inline-block px-3 py-1 text-xs uppercase tracking-wide bg-primary/10 text-primary rounded-none mb-2">
                          {activity.type || 'Upload de Projeto'}
                        </span>
                        <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{activity.name}</h3>
                        {activity.description && <p className="text-muted-foreground">{activity.description}</p>}
                      </div>
                      {submission && submission.status === 'approved' && (
                        <div className="flex items-center gap-2 text-sm font-medium text-green-600 dark:text-green-400">
                          <CheckCircle size={16} />
                          Aprovado {submission.grade && `(${submission.grade}/100)`}
                        </div>
                      )}
                    </div>

                    {submission ? (
                      <div className="border border-border rounded-none p-6 bg-muted/30">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <FileText className="h-5 w-5 text-muted-foreground" />
                            <div>
                              <p className="font-medium">{submission.file_name}</p>
                              <p className="text-xs text-muted-foreground">
                                Enviado em {new Date(submission.submitted_at).toLocaleDateString('pt-BR')}
                              </p>
                            </div>
                          </div>
                          <span className={`px-3 py-1 text-xs uppercase tracking-wide rounded-none ${submission.status === 'approved' ? 'bg-green-500/10 text-green-600 dark:text-green-400' :
                            submission.status === 'rejected' ? 'bg-red-500/10 text-red-600 dark:text-red-400' :
                              'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
                            }`}>
                            {submission.status === 'approved' ? 'Aprovado' : submission.status === 'rejected' ? 'Rejeitado' : 'Pendente'}
                          </span>
                        </div>
                        {submission.feedback && (
                          <div className="mt-4 p-4 bg-background rounded-none border border-border">
                            <p className="text-sm font-medium mb-1">Feedback do Instrutor:</p>
                            <p className="text-sm text-muted-foreground">{submission.feedback}</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <select
                          className="flex h-10 w-full rounded-none border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          value={evidenceCategories[activity.id || `activity_${index}`] || ''}
                          onChange={(e) => setEvidenceCategories(prev => ({ ...prev, [activity.id || `activity_${index}`]: e.target.value }))}
                        >
                          <option value="">Qual Conquista isso representa? (Opcional)...</option>
                          {VICTORY_TYPES.map(type => (
                            <option key={type.id} value={type.id} title={type.label}>
                              {type.label}
                            </option>
                          ))}
                        </select>

                        <label
                          htmlFor={`file-upload-${index}`}
                          className="border-2 border-dashed border-border rounded-none p-8 text-center hover:bg-muted/30 transition-colors cursor-pointer group block"
                        >
                          <Upload className="mx-auto text-muted-foreground group-hover:text-primary mb-3 transition-colors" size={32} strokeWidth={1.5} />
                          <p className="font-medium mb-1">Arraste seu arquivo aqui ou clique para selecionar</p>
                          <p className="text-xs text-muted-foreground">PDF, PPTX, DOCX ou ZIP (Máx 50MB)</p>
                          <input
                            id={`file-upload-${index}`}
                            type="file"
                            className="hidden"
                            accept=".pdf,.pptx,.docx,.zip,.txt"
                            onChange={(e) => handleFileUpload(activity.id || `activity_${index}`, e.target.files[0])}
                            disabled={uploadingActivity === (activity.id || `activity_${index}`)}
                          />
                        </label>
                        {uploadingActivity === (activity.id || `activity_${index}`) && (
                          <p className="text-center text-sm text-muted-foreground mt-4">Enviando arquivo...</p>
                        )}
                      </div>
                    )}
                  </Card>
                );
              })
            ) : (
              <Card className="p-8 rounded-none border border-border">
                <p className="text-muted-foreground text-center">Nenhuma atividade cadastrada para este módulo.</p>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="references" className="space-y-6">
            {module.references && module.references.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {module.references.map((ref, index) => (
                  <Card key={index} className="p-6 rounded-none border border-border hover:border-primary/50 transition-colors">
                    <h4 className="font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{ref.title}</h4>
                    {ref.author && <p className="text-sm text-muted-foreground mb-2">{ref.author}</p>}
                    {ref.url && (
                      <a
                        href={ref.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-medium uppercase tracking-wide hover:underline"
                      >
                        Acessar <LinkIcon className="h-3 w-3" />
                      </a>
                    )}
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-8 rounded-none border border-border">
                <p className="text-muted-foreground text-center">Referências serão disponibilizadas em breve.</p>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>

      <VictoryCelebration
        achievement={unlockedAchievement}
        onClose={() => setUnlockedAchievement(null)}
      />
    </div>
  );
}